import React, { useCallback, useState, createContext, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "./useLocalStorage";
import {
  addStore,
  loginRest,
  signupRestaurant,
  ListStore,
  verifyOtpRest, // Ensure this is exported from your apiHandler.js
  forgotPasswordRequestRest,
  resetPasswordRest
} from "./apiHandler";
import { useSnackbar } from "./SnackbarProvider";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useLocalStorage("user", null);
  const [, setda] = useLocalStorage('da', null);
  const [, setStore] = useLocalStorage('ds', null);
  const [listStore, setListStore] = useState([]);
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();

  const Logout = useCallback((stateCode) => {
    console.warn('Logout triggered with state:', stateCode);
    if (stateCode === 500) {
      navigate("/admin", { replace: true });
    } else if (stateCode === 403) {
      setUser(null);
      setda(null);
      setStore(null);
      navigate("/store", { replace: true });
    } else {
      setUser(null);
      setda(null);
      setStore(null);
      navigate("/login", { replace: true });
    }
  }, [setUser, setStore, setda, navigate]);

  /**
   * STEP 1: Initial Login
   * Checks credentials and triggers OTP generation on the backend.
   */
  const login = useCallback(async (data) => {
    try {
      const resp = await loginRest(data, Logout);

      if (resp.code === 0) {
        // If backend says verification is required, we don't set user yet
        if (resp.data?.step === 'verification_required') {
          showSnackbar(resp.msg || 'Verification code sent to email', 'info');
          return resp; // Success Step 1
        }

        // Legacy/Direct login fallback (if no OTP required)
        // await setUser(resp.data);
        // showSnackbar('Login successful!', 'success');
        // return true;
      } else {
        showSnackbar(resp.msg || 'Login failed', 'error');
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      showSnackbar(error?.msg || 'Internal Server Error', 'error');
      return false;
    }
  }, [setUser, showSnackbar, Logout]);

  /**
   * STEP 2: Verify OTP
   * Validates the 6-digit code and receives the final JWT.
   */
  const verifyOTP = useCallback(async ({ email, otp, type }) => {
    try {
      // Calling the backend endpoint you provided in the router (/verify-otp)
      const resp = await verifyOtpRest({ email, otp, type }, Logout);

      if (resp && resp.code === 0) {
        // Verification successful: resp.data contains { jwt, sAccess }
        await setUser(resp.data);
        showSnackbar(resp?.msg || 'Verification successful. Welcome!', 'success');
        return resp;
      } else {
        showSnackbar(resp?.msg || 'Invalid verification code', 'error');
        return resp;
      }
    } catch (error) {
      console.error("OTP Verification Error:", error);
      showSnackbar('Verification failed. Please try again.', 'error');
      return { code: 4, msg: 'Error' };
    }
  }, [setUser, showSnackbar, Logout]);

  const signup = useCallback(async (data) => {
    try {
      const resp = await signupRestaurant(data);
      if (resp.code === 0) {
        showSnackbar('Account created successfully!', 'success');
        return true;
      } else {
        showSnackbar('Error: ' + resp.msg, 'error');
      }
    } catch (error) {
      showSnackbar(error.msg || 'Signup failed', 'error');
    }
  }, [showSnackbar]);

  const updateStoreList = useCallback(async () => {
    const resp = await ListStore(Logout);
    if (resp?.data) setListStore(resp.data);
  }, [Logout]);

  const forgotPasswordRequest = useCallback(async (data) => {
    try {
      const resp = await forgotPasswordRequestRest(data, Logout);
      if (resp?.code === 0) {
        showSnackbar(resp.msg || 'Reset code sent to your email', 'info');
      } else {
        showSnackbar(resp?.msg || 'Failed to send reset code', 'error');
      }
      return resp;
    } catch (error) {
      showSnackbar('Error processing request', 'error');
    }
  }, [showSnackbar, Logout]);

  
  /**
   * FORGOT PASSWORD - STEP 3: Update Password
   */
  const resetPassword = useCallback(async (data) => {
    try {
      const resp = await resetPasswordRest(data, Logout);
      if (resp?.code === 0) {
        showSnackbar('Password updated successfully. Please log in.', 'success');
      } else {
        showSnackbar(resp?.msg || 'Failed to update password', 'error');
      }
      return resp;
    } catch (error) {
      showSnackbar('Reset failed', 'error');
    }
  }, [showSnackbar, Logout]);


  const addcmpny = useCallback(async (data) => {
    try {
      const resp = await addStore(data, Logout);
      if (resp.code === 0) {
        setListStore(resp.data);
        await setUser(resp.newLoad);
        showSnackbar('Store added successfully!', 'success');
        return true;
      } else {
        showSnackbar('Error: ' + resp.msg, 'error');
      }
    } catch (error) {
      showSnackbar(error.msg || 'Failed to add store', 'error');
    }
  }, [showSnackbar, Logout, setUser]);

  const value = useMemo(
    () => ({
      user,
      listStore,
      login,
      verifyOTP,
      signup,
      Logout,
      addcmpny,
      updateStoreList,
      forgotPasswordRequest,
      resetPassword
    }),
    [user, listStore, login, verifyOTP, signup, Logout, addcmpny, updateStoreList]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};